// Step 1 of the New-alarm flow (UI-3): the recipe LIBRARY as a grid of cards.
// Each card shows the recipe title, description, default-severity chip, and an
// honest compile-support badge — a `pending` recipe is offered but plainly
// labelled "Won't evaluate yet" so the operator isn't surprised when the saved
// alarm doesn't fire. An added hint marks recipes that already have an active
// definition on this boat; it is a HINT, not a hard block — the server's 409 is
// the real backstop, so an operator can still open it and be told precisely.
//
// The hint's WORDING depends on whether the recipe can hold more than one alarm.
// A recipe with a path family pins to a concrete instance, so a boat with five
// tanks wants five tank alarms (task recipe_per_instance); "Added" there would
// read as "you already have this" and talk the owner out of the thing they came
// to do. Those cards count ("2 added"). A fixed-path recipe really is
// one-per-boat and keeps the plain word.
//
// The grid renders TWICE on the page: once for the recipes this boat can use,
// and once inside the disclosure for the ones it cannot (see
// `recipeAvailability.ts`). The second pass passes `reasonByRecipe`, which
// replaces the compile-support badge with the specific reason — "we can't run
// this yet", "your boat isn't reporting this" and "needs a newer version on your
// boat" have three different fixes, and one badge could only ever say the first.
//
// The third of those is also the only reason that DISABLES a card. The other two
// still save (the alarm is stored and compiles honest-pending, which is what an
// owner about to wire a bilge switch wants); a recipe the boat's own bundled
// compiler predates is refused by the server, so its form is a dead end.
//
// SUPERSEDED recipes never reach here at all — `groupRecipes` drops them from
// both groups. See its comment for why the disclosure is the wrong home for one.

import { alarmSeverityLabel, type AlarmSeverity } from '../../lib/alarmDisplay';
import type { AlertTemplate } from '../../lib/alertTemplates';
import { isPickable, unavailableLabel, type UnavailableRecipe } from '../../lib/recipeAvailability';

/** What this boat already has for one catalog recipe. */
export interface ExistingRecipeUse {
  /** Active definitions across ALL instances of this recipe. */
  count: number;
  /** One of their definition ids — only meaningful to link to when count is 1. */
  firstId: string;
}

interface RecipePickerProps {
  templates: readonly AlertTemplate[];
  /** BASE recipe id → what this boat already has for it (drives the added hint). */
  existingByRecipe: ReadonlyMap<string, ExistingRecipeUse>;
  onPick: (template: AlertTemplate) => void;
  /**
   * recipeId → why this boat cannot use it. Passed only for the tucked-away
   * grid; absent ⇒ every card here is one the boat can use, and the card falls
   * back to the plain compile-support badge.
   */
  reasonByRecipe?: ReadonlyMap<string, UnavailableRecipe['reason']>;
  /** Distinguishes the two grids' test ids and the cards' aria labelling. */
  testId?: string;
}

export function RecipePicker({
  templates,
  existingByRecipe,
  onPick,
  reasonByRecipe,
  testId = 'recipe-picker',
}: RecipePickerProps) {
  return (
    <ul className="recipe-grid" data-testid={testId}>
      {templates.map((t) => {
        const existing = existingByRecipe.get(t.recipeId);
        const severity = t.defaultSeverity as AlarmSeverity;
        const reason = reasonByRecipe?.get(t.recipeId);
        // `singleMember` marks a fixed-path family (GPS position lost, anchor
        // drift): one literal path, so one alarm. Everything else — tanks,
        // batteries, engines — holds one per instance. See the header comment
        // for why the wording differs.
        const multiInstance = t.pathFamily !== undefined && t.pathFamily.singleMember !== true;
        const addedLabel =
          existing === undefined ? null : multiInstance ? `${existing.count} added` : 'Added';
        // A recipe the boat's own software cannot compile is the one reason a
        // card leads NOWHERE: the server refuses the create, so opening the form
        // would walk the owner through every threshold and then fail on save.
        // Disabled rather than hidden — "your boat could do this after an
        // update" is worth knowing, and a recipe that simply vanished reads as a
        // missing feature.
        const pickable = reason === undefined || isPickable(reason);
        return (
          <li key={t.recipeId} className="recipe-card">
            <button
              type="button"
              className="recipe-card__btn"
              data-testid={`recipe-card-${t.recipeId}`}
              disabled={!pickable}
              onClick={() => onPick(t)}
            >
              <div className="recipe-card__head">
                <span className={`alarm-sev alarm-sev--${severity}`}>
                  {alarmSeverityLabel(severity)}
                </span>
                {addedLabel !== null ? (
                  <span
                    className="alarm-chip alarm-chip--muted"
                    data-testid={`recipe-added-${t.recipeId}`}
                  >
                    {addedLabel}
                  </span>
                ) : null}
                {reason !== undefined ? (
                  <span
                    className="alarm-chip alarm-chip--attention"
                    data-testid={`recipe-unavailable-${t.recipeId}`}
                  >
                    {unavailableLabel(reason)}
                  </span>
                ) : t.compileSupport === 'pending' ? (
                  <span
                    className="alarm-chip alarm-chip--attention"
                    data-testid={`recipe-pending-${t.recipeId}`}
                  >
                    Won&apos;t evaluate yet
                  </span>
                ) : null}
              </div>
              <h3 className="recipe-card__title">{t.title}</h3>
              <p className="recipe-card__desc">{t.description}</p>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
